import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidationResult } from '../../utils/mediaValidation';
import { StoryContent } from './StoryContent';
import { ReelsContent } from './ReelsContent';
import { CarouselContent, CarouselMediaItem } from './CarouselContent';
import { inferFeedPublicationFormat } from '@/features/scheduler/utils/carouselMedia';

interface ContentSectionProps {
  publicationType: string;
  setPublicationType: (type: string) => void;
  onValidationChange?: (isValid: boolean, file: File | null, validation: ValidationResult | null) => void;
  onTagsChange?: (tags: string[]) => void;
  onCaptionChange?: (caption: string) => void;
  caption?: string;
  reelsCoverFile?: File | null;
  onReelsCoverFileChange?: (file: File | null) => void;
  shareToFeed?: boolean;
  onShareToFeedChange?: (shareToFeed: boolean) => void;
  carouselItems?: CarouselMediaItem[];
  onCarouselItemsChange?: (items: CarouselMediaItem[]) => void;
  onCarouselOverflow?: () => void;
  disabled?: boolean;
}

export const ContentSection: React.FC<ContentSectionProps> = ({
  publicationType,
  setPublicationType,
  onValidationChange,
  onTagsChange,
  onCaptionChange,
  caption,
  reelsCoverFile,
  onReelsCoverFileChange,
  shareToFeed,
  onShareToFeedChange,
  carouselItems,
  onCarouselItemsChange,
  onCarouselOverflow,
  disabled,
}) => {
  const { t } = useTranslation();
  const publicationTypeRef = useRef<HTMLElement | null>(null);
  const feedItems = carouselItems ?? [];
  const feedFormat = inferFeedPublicationFormat(feedItems);

  useEffect(() => {
    const publicationTypeSelect = publicationTypeRef.current;
    if (!publicationTypeSelect) return undefined;

    const handlePublicationTypeChange = (event: Event) => {
      const value = (event as CustomEvent<{ value?: string }>).detail?.value;
      if (value === 'stories' || value === 'feed') setPublicationType(value);
    };

    publicationTypeSelect.addEventListener('bdsChange', handlePublicationTypeChange);
    return () => publicationTypeSelect.removeEventListener('bdsChange', handlePublicationTypeChange);
  }, [setPublicationType]);

  const getFeedPublicationNotice = () => {
    switch (feedFormat) {
      case 'image':
        return t('feedPublicationInfo.singleImage');
      case 'reels':
        return t('feedPublicationInfo.reel');
      case 'carousel':
        return t('feedPublicationInfo.carousel');
      default:
        return t('feedPublicationInfo.empty');
    }
  };

  const getPublicationTypeLabel = (type: string) => {
    switch (type) {
      case 'feed':
        return t('publicationTypes.feed');
      case 'stories':
        return t('publicationTypes.stories');
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const renderContentByType = () => {
    switch (publicationType) {
      case 'stories':
        return (
          <StoryContent
            onValidationChange={onValidationChange}
            onTagsChange={onTagsChange}
          />
        );
      case 'feed': {
        return (
          <>
            <CarouselContent
              items={feedItems}
              onItemsChange={onCarouselItemsChange}
              onOverflow={onCarouselOverflow}
              onCaptionChange={onCaptionChange}
              caption={caption}
              showCaption={feedFormat !== 'reels'}
              disabled={disabled}
            />
            <ReelsContent
              onValidationChange={onValidationChange}
              onCaptionChange={onCaptionChange}
              caption={caption}
              coverFile={reelsCoverFile}
              onCoverFileChange={onReelsCoverFileChange}
              shareToFeed={shareToFeed}
              onShareToFeedChange={onShareToFeedChange}
              hideMediaUpload
              showReelsOptions={feedFormat === 'reels'}
              disabled={disabled}
            />
          </>
        );
      }
      default:
        return null;
    }
  };

  return (
    <bds-paper class="scheduler-section scheduler-content-section" border width="auto">
      <bds-grid direction="column" gap="2" lg="6" md="6" sm="6" xs="6" padding="none">
        <div className="scheduler-content-section_header">
          <bds-typo class="scheduler-content-section_header-title" tag="h4" variant="fs-20" bold="bold" margin={false}>
            {t('content')}
          </bds-typo>
        </div>
        
        <bds-select
          ref={publicationTypeRef as any}
          label={t('publicationType')}
          value={publicationType}
          placeholder={t('selectPublicationType')}
        >
          {['stories', 'feed'].map((type) => (
            <bds-select-option 
              key={type}
              value={type}
            >
              {getPublicationTypeLabel(type)}
            </bds-select-option>
          ))}
        </bds-select>

        {publicationType === 'stories' && (
          <bds-typo class="scheduler-content-section_notice" tag="p" variant="fs-12">
            {t('storiesStickerRestrictionNotice')}
          </bds-typo>
        )}
        {publicationType === 'feed' && (
          <bds-typo class="scheduler-content-section_notice" tag="p" variant="fs-12">
            {getFeedPublicationNotice()}
          </bds-typo>
        )}

        {renderContentByType()}
      </bds-grid>
    </bds-paper>
  );
};